/**
 * Loading and a page that could not be read (redesign §33, #120).
 *
 * Loading shows nothing for 300 ms — most reads land before then, and a
 * skeleton that flashes is worse than none — then the skeleton of the shape to
 * come in #F5F5F7 bars, radius 4, 10 high, pulsing o 1 ↔ .6 over 1.2 s, with
 * `aria-busy`. Once shown it stays at least 400 ms, so it never blinks. Under
 * reduced motion the bars hold still (globals.css).
 *
 * A read that failed takes the content's place with the product's sentence,
 * one Try again that reads « Retrying » while it runs, and `role="alert"`; a
 * second failure says the same thing again, never louder. Never an amber band.
 */
import { useEffect, useRef, useState } from 'react';
import type React from 'react';
import { color, font } from '../theme/tokens';
import { PrimaryButton } from './primitives';
import { AirlockMark } from './AirlockMark';

/** What a page shows while it reads: nothing yet, the skeleton, or its content. */
export type LoadPhase = 'quiet' | 'skeleton' | 'content';

export function useLoadPhase(loading: boolean): LoadPhase {
  const [phase, setPhase] = useState<LoadPhase>(loading ? 'quiet' : 'content');
  const shownAt = useRef(0);
  useEffect(() => {
    if (loading) {
      setPhase((was) => (was === 'skeleton' ? was : 'quiet'));
      const timer = window.setTimeout(() => {
        shownAt.current = performance.now();
        setPhase('skeleton');
      }, 300);
      return () => window.clearTimeout(timer);
    }
    if (phase !== 'skeleton') {
      setPhase('content');
      return;
    }
    const left = 400 - (performance.now() - shownAt.current);
    const timer = window.setTimeout(() => setPhase('content'), Math.max(0, left));
    return () => window.clearTimeout(timer);
    // `phase` is read, not followed: only a change of `loading` decides.
  }, [loading]);
  return phase;
}

function Bar({ width }: { width: string | number }): React.JSX.Element {
  return <span className="al-skeleton" style={{ display: 'block', width, height: 10, borderRadius: 4, background: '#F5F5F7' }} />;
}

/** Skeleton rows of a table: one bar per column, at the table's own row height. */
export function SkeletonRows({ rows = 4, columns }: { rows?: number; columns: string }): React.JSX.Element {
  const count = columns.trim().split(/\s+(?![^(]*\))/).length;
  return (
    <div aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, row) => (
        <div
          key={row}
          style={{
            display: 'grid',
            gridTemplateColumns: columns,
            gap: 16,
            alignItems: 'center',
            minHeight: 40,
            padding: '8px 12px',
            borderBottom: `1px solid ${color.line}`
          }}
        >
          {Array.from({ length: count }, (_, cell) => (
            <Bar key={cell} width={cell === 0 ? '70%' : '50%'} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Skeleton blocks of a page: a band of figures, then a few lines. */
export function SkeletonPage({ blocks = 3 }: { blocks?: number }): React.JSX.Element {
  return (
    <div aria-busy="true" aria-label="Loading" style={{ display: 'grid', gap: 28 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 24 }}>
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} style={{ display: 'grid', gap: 10 }}>
            <Bar width="40%" />
            <Bar width="70%" />
          </div>
        ))}
      </div>
      {Array.from({ length: blocks }, (_, i) => (
        <div key={i} style={{ display: 'grid', gap: 10 }}>
          <Bar width="30%" />
          <Bar width="90%" />
          <Bar width="75%" />
        </div>
      ))}
    </div>
  );
}

/** The page's content could not be read (#120): it says so, says nothing changed, and offers one retry. */
export function PageError({ onRetry }: { onRetry: () => Promise<unknown> | void }): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  return (
    <div role="alert" style={{ padding: '40px 0 48px', maxWidth: 480 }}>
      <div style={{ fontSize: 17, fontWeight: 600, color: color.ink }}>Something went wrong loading this page</div>
      <p style={{ fontSize: 13, lineHeight: 1.5, color: '#6E6E73', margin: '6px 0 16px' }}>
        Nothing has changed. Try again in a moment.
      </p>
      <PrimaryButton
        tone="ink"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void Promise.resolve(onRetry()).finally(() => setBusy(false));
        }}
      >
        {busy ? 'Retrying' : 'Try again'}
      </PrimaryButton>
    </div>
  );
}

/**
 * The first read of the session (§37.2): a white page with no header, since
 * nobody is known yet. Quiet, it is empty; shown, the mark and the product's
 * « airlock · one moment » fade in over 120 ms and pulse o 1 ↔ .6 in 1.2 s,
 * still under reduced motion.
 */
export function BootLoading({ shown }: { shown: boolean }): React.JSX.Element {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={shown ? undefined : 'Loading'}
      style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: color.page }}
    >
      {shown && (
        <div className="al-fade-in">
          <div className="al-skeleton" style={{ display: 'flex', alignItems: 'center', gap: 10, color: color.inkSubtle }}>
            <AirlockMark size={24} />
            <span style={{ fontFamily: font.mono, fontSize: 12.5 }}>airlock · one moment</span>
          </div>
        </div>
      )}
    </div>
  );
}
